import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PlateformService from '../../components/PlateformService';
import ProductService from '../../components/ProductService';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Button,
  Avatar,
  Chip,
  Alert,
} from '@mui/material';

const prettyPrint = (data) => JSON.stringify(data, null, 2);

const normalizeId = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return (
      value.$oid ||
      value._id ||
      value.$id ||
      value.id ||
      value.value ||
      value
    );
  }

  return String(value);
};

const normalizeName = (value = '') =>
  value
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const cleanThumbUrl = (thumb) => {
  if (!thumb || typeof thumb !== 'string') {
    return '';
  }

  const sanitized = thumb.replace(/\\"/g, '"');
  const segments = sanitized.split(/\s|"/).filter(Boolean);
  const base = segments[0] || sanitized;

  try {
    const url = new URL(base);
    url.pathname = url.pathname
      .split('/')
      .map((segment) => encodeURIComponent(decodeURIComponent(segment)))
      .join('/');
    return url.toString();
  } catch (error) {
    return encodeURI(base);
  }
};

const buildVendorImage = (thumb) => {
  const baseUrl = "https://aimer.ma/image/cache/";

  // Encode URL parts safely
  const encodedPath = thumb
    .split('/')
    .map(encodeURIComponent) // encode every folder and file
    .join('/');

  // Insert -800x800 before .jpg or .png
  const upgradedSize = encodedPath.replace(/(\.[a-zA-Z]+)$/, "-800x800$1");

  return baseUrl + upgradedSize;
};


const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);

const extractProductImage = (product) => {
  const basePath = process.env.REACT_APP_DATA_PIC_PATH || '';

  const pickImage = () => {
    if (product?.url_product) {
      return product.url_product;
    }

    if (Array.isArray(product?.gallery_images) && product.gallery_images.length > 0) {
      return product.gallery_images[0];
    }

    return '';
  };

  const imagePath = pickImage();
  if (!imagePath) return '';

  if (isAbsoluteUrl(imagePath)) {
    return imagePath;
  }

  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const normalizedImage = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${normalizedBase}/${normalizedImage}`;
};

const buildLoginPayload = (form) => {
  const payload = {};

  if (form.plateform_id.trim()) {
    payload.plateform_id = form.plateform_id.trim();
  }

  if (form.plateform_name.trim()) {
    payload.plateform_name = form.plateform_name.trim();
  }

  if (form.login.trim()) {
    payload.login = form.login.trim();
  }

  if (form.password.trim()) {
    payload.password = form.password.trim();
  }

  if (form.forceLogin) {
    payload.forceLogin = true;
  }

  return payload;
};

function AimerPlatformConsole() {
  const location = useLocation();
  const initialPlateformId = location?.state?.plateformId || '';
  const initialPlateformName = location?.state?.plateformName || 'aimer.ma';
  const initialAimerData =
    location?.state?.plateformData?.additionnal_data?.aimer || null;

  const [loginForm, setLoginForm] = useState({
    plateform_id: initialPlateformId,
    plateform_name: initialPlateformName,
    login: '',
    password: '',
    forceLogin: false,
  });

  const [loginResult, setLoginResult] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [productId, setProductId] = useState('');
  const [productResult, setProductResult] = useState(null);
  const [productError, setProductError] = useState('');
  const [productLoading, setProductLoading] = useState(false);

  const [categorySyncResult, setCategorySyncResult] = useState(null);
  const [categorySyncError, setCategorySyncError] = useState('');
  const [categorySyncLoading, setCategorySyncLoading] = useState(false);

  const [vendorProducts, setVendorProducts] = useState([]);
  const [vendorError, setVendorError] = useState('');
  const [vendorLoading, setVendorLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [linkingTarget, setLinkingTarget] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loginPayloadPreview = useMemo(
    () => prettyPrint(buildLoginPayload(loginForm)),
    [loginForm]
  );

  const hasBearerToken = useMemo(() => {
    if (!initialAimerData) return false;

    return Boolean(
      initialAimerData.bearer ||
        initialAimerData.bearer_token ||
        initialAimerData.access_token
    );
  }, [initialAimerData]);

  const handleLoginInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setLoginForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError('');
    setLoginResult(null);

    try {
      setLoginLoading(true);
      const payload = buildLoginPayload(loginForm);
      const response = await PlateformService.loginAimer(payload);
      setLoginResult(response);
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Impossible de se connecter à aimer.ma.';
      setLoginError(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleProductSubmit = async (event) => {
    event.preventDefault();
    const trimmedId = productId.trim();

    if (!trimmedId) {
      setProductError('Veuillez saisir un identifiant produit.');
      setProductResult(null);
      return;
    }

    setProductError('');
    setProductResult(null);

    try {
      setProductLoading(true);
      const response = await PlateformService.fetchAimerProduct(trimmedId);
      setProductResult(response);
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Impossible de récupérer les détails de l'article.";
      setProductError(message);
    } finally {
      setProductLoading(false);
    }
  };

  const handleCategorySync = async () => {
    setCategorySyncError('');
    setCategorySyncResult(null);

    try {
      setCategorySyncLoading(true);
      const response = await PlateformService.syncAimerVendorCategories();
      setCategorySyncResult(response);
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Impossible de synchroniser les catégories vendeur.';
      setCategorySyncError(message);
    } finally {
      setCategorySyncLoading(false);
    }
  };

  const fetchLocalProducts = async () => {
    try {
      const response = await ProductService.fetchProducts();
      // fetchProducts now returns an array directly from NDJSON stream
      const list = Array.isArray(response) ? response : [];
      setAllProducts(list);
    } catch (error) {
      console.error(error);
      setVendorError((prev) =>
        prev || 'Impossible de récupérer les produits internes.'
      );
    }
  };

  const handleVendorFetch = async () => {
    setVendorError('');

    try {
      setVendorLoading(true);
      const response = await PlateformService.fetchAimerVendorProducts();
      if (response?.products && Array.isArray(response.products)) {
        setVendorProducts(response.products);
      } else {
        setVendorProducts([]);
      }
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Impossible de récupérer les produits vendeur.';
      setVendorError(message);
    } finally {
      setVendorLoading(false);
    }
  };

  useEffect(() => {
    fetchLocalProducts();
    handleVendorFetch();
  }, []);

  const aimerPlateformId = useMemo(
    () => normalizeId(loginForm.plateform_id || initialPlateformId),
    [initialPlateformId, loginForm.plateform_id]
  );

  const aimerPlateformName = useMemo(
    () => loginForm.plateform_name || initialPlateformName,
    [initialPlateformName, loginForm.plateform_name]
  );

  const hasAimerPlateformLink = useCallback(
    (product) => {
      const plateforms = Array.isArray(product?.plateforms)
        ? product.plateforms
        : [];

      return plateforms.some((entry) => {
        const entryName = entry?.plateform_name || entry?.name;
        const entryId = normalizeId(
          entry?.plateform_id || entry?.id || entry?._id || entry?.value
        );
        const normalizedEntryName = entryName?.toString()?.toLowerCase?.();

        if (normalizedEntryName && normalizedEntryName.includes('aimer')) {
          return true;
        }

        if (entryId && aimerPlateformId && entryId === aimerPlateformId) {
          return true;
        }

        return false;
      });
    },
    [aimerPlateformId]
  );

  const unmatchedLocalProducts = useMemo(
    () =>
      Array.isArray(allProducts)
        ? allProducts.filter((product) => !hasAimerPlateformLink(product))
        : [],
    [allProducts, hasAimerPlateformLink]
  );

  const matchedVendorProducts = useMemo(() => {
    if (!Array.isArray(vendorProducts) || vendorProducts.length === 0) {
      return [];
    }

    return vendorProducts
      .map((vendorProduct) => {
        const vendorName = normalizeName(vendorProduct?.name);
        if (!vendorName) {
          return null;
        }

        const matches = unmatchedLocalProducts.filter(
          (product) => normalizeName(product?.name) === vendorName
        );

        if (!matches.length) {
          return null;
        }

        return {
          vendor: vendorProduct,
          matches,
        };
      })
      .filter(Boolean);
  }, [unmatchedLocalProducts, vendorProducts]);

  const paginatedMatches = useMemo(
    () =>
      matchedVendorProducts.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [matchedVendorProducts, page, rowsPerPage]
  );

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= matchedVendorProducts.length) {
      setPage(0);
    }
  }, [matchedVendorProducts.length, page, rowsPerPage]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLinkProduct = async (localProductId, vendorProduct) => {
    if (!localProductId || !vendorProduct?.product_id) {
      setVendorError('Produit ou identifiant distant manquant.');
      return;
    }

    if (!aimerPlateformId) {
      setVendorError('Identifiant de plateforme aimer.ma manquant.');
      return;
    }

    setVendorError('');

    const linkKey = `${vendorProduct.product_id}-${localProductId}`;
    setLinkingTarget(linkKey);

    try {
      await PlateformService.linkProductToPlateform(localProductId, {
        plateform_id: aimerPlateformId,
        remote_id: vendorProduct.product_id,
      });

      setVendorProducts((prev) =>
        Array.isArray(prev)
          ? prev.filter((item) => item.product_id !== vendorProduct.product_id)
          : []
      );

      setAllProducts((prev) =>
        Array.isArray(prev)
          ? prev.map((product) => {
              const productId = product._id || product.id || product.value;
              if (productId !== localProductId) {
                return product;
              }

              const existingPlateforms = Array.isArray(product.plateforms)
                ? product.plateforms
                : [];

              return {
                ...product,
                plateforms: [
                  ...existingPlateforms,
                  {
                    plateform_id: aimerPlateformId,
                    plateform_name: aimerPlateformName,
                    remote_id: vendorProduct.product_id,
                  },
                ],
              };
            })
          : []
      );
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        'Impossible de lier le produit à aimer.ma.';
      setVendorError(message);
    } finally {
      setLinkingTarget('');
    }
  };

  const totalMatchCount = matchedVendorProducts.length;

  return (
    <div className="container my-4">
      <h1 className="mb-4">Plateforme aimer.ma</h1>

      {hasBearerToken && (
        <section className="mb-5">
          <h2 className="h4">Connexion</h2>
          <div className="alert alert-success" role="alert">
            Jeton Aimer détecté. La connexion a déjà été réalisée pour cette
            plateforme.
          </div>
        </section>
      )}

      {!hasBearerToken && (
        <section className="mb-5">
          <h2 className="h4">Connexion</h2>
          <p className="text-muted">
            Déclenchez une connexion au compte aimer.ma sélectionné et consultez
            la réponse de l&apos;API.
          </p>
          <form onSubmit={handleLoginSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="plateform_id">
                Identifiant plateforme
              </label>
              <input
                id="plateform_id"
                name="plateform_id"
                className="form-control"
                value={loginForm.plateform_id}
                onChange={handleLoginInputChange}
                placeholder="Identifiant (optionnel)"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="plateform_name">
                Nom plateforme
              </label>
              <input
                id="plateform_name"
                name="plateform_name"
                className="form-control"
                value={loginForm.plateform_name}
                onChange={handleLoginInputChange}
                placeholder="Nom de la plateforme"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="login">
                Identifiant de connexion
              </label>
              <input
                id="login"
                name="login"
                className="form-control"
                value={loginForm.login}
                onChange={handleLoginInputChange}
                placeholder="Login (optionnel)"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="password">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                name="password"
                className="form-control"
                value={loginForm.password}
                onChange={handleLoginInputChange}
                placeholder="Mot de passe (optionnel)"
              />
            </div>
            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="forceLogin"
                  name="forceLogin"
                  checked={loginForm.forceLogin}
                  onChange={handleLoginInputChange}
                />
                <label className="form-check-label" htmlFor="forceLogin">
                  Forcer une reconnexion (ignore le cache de jeton)
                </label>
              </div>
            </div>
            <div className="col-12">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loginLoading}
              >
                {loginLoading ? 'Connexion en cours…' : 'Se connecter'}
              </button>
            </div>
          </form>
          <div className="mt-3">
            <h3 className="h6">Payload préparé</h3>
            <pre className="bg-light p-3 rounded">{loginPayloadPreview}</pre>
          </div>
          {loginError && (
            <div className="alert alert-danger mt-3" role="alert">
              {loginError}
            </div>
          )}
          {loginResult && (
            <div className="alert alert-success mt-3" role="alert">
              <p className="mb-2">Connexion réussie :</p>
              <pre className="bg-light p-3 rounded mb-0">
                {prettyPrint(loginResult)}
              </pre>
            </div>
          )}
        </section>
      )}

      <section className="mb-5">
        <Paper elevation={1} className="p-3">
          <Stack spacing={2}>
            <div>
              <Typography variant="h5" component="h2" gutterBottom>
                Catégories vendeur
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Synchronisez les catégories vendeur depuis aimer.ma et créez les
                correspondances locales.
              </Typography>
            </div>
            <div>
              <Button
                variant="outlined"
                onClick={handleCategorySync}
                disabled={categorySyncLoading}
              >
                {categorySyncLoading ? 'Synchronisation…' : 'Sync categories'}
              </Button>
            </div>
            {categorySyncError && (
              <Alert severity="error">{categorySyncError}</Alert>
            )}
            {categorySyncResult && (
              <Alert severity="info">
                <Typography gutterBottom component="p" className="mb-2">
                  Synchronisation terminée :
                </Typography>
                <pre className="bg-light p-3 rounded mb-0">
                  {prettyPrint(categorySyncResult)}
                </pre>
              </Alert>
            )}
          </Stack>
        </Paper>
      </section>

      <section className="mb-5">
        <h2 className="h4">Produit par identifiant</h2>
        <form onSubmit={handleProductSubmit} className="row g-3 align-items-end">
          <div className="col-md-6">
            <label className="form-label" htmlFor="productId">
              Identifiant produit aimer.ma
            </label>
            <input
              id="productId"
              className="form-control"
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              placeholder="Saisissez l'identifiant produit"
            />
          </div>
          <div className="col-auto">
            <button
              type="submit"
              className="btn btn-secondary"
              disabled={productLoading}
            >
              {productLoading ? 'Chargement…' : 'Rechercher'}
            </button>
          </div>
        </form>
        {productError && (
          <div className="alert alert-danger mt-3" role="alert">
            {productError}
          </div>
        )}
        {productResult && (
          <div className="alert alert-info mt-3" role="alert">
            <p className="mb-2">Produit récupéré :</p>
            <pre className="bg-light p-3 rounded mb-0">
              {prettyPrint(productResult)}
            </pre>
          </div>
        )}
      </section>

      <section className="mb-5">
        <h2 className="h4">Produits vendeur</h2>
        <p className="text-muted">
          Récupère l&apos;ensemble des produits vendeur avec pagination automatique et
          propose un rapprochement par nom avec vos produits internes qui ne
          sont pas encore liés à aimer.ma.
        </p>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleVendorFetch}
          disabled={vendorLoading}
        >
          {vendorLoading ? 'Chargement…' : 'Charger les produits vendeur'}
        </button>
        {totalMatchCount > 0 && (
          <div className="mt-3">
            <Chip
              color="primary"
              label={`${totalMatchCount} produit${totalMatchCount > 1 ? 's' : ''} avec correspondance`}
            />
          </div>
        )}
        {vendorError && (
          <div className="alert alert-danger mt-3" role="alert">
            {vendorError}
          </div>
        )}
        {totalMatchCount === 0 && !vendorLoading && (
          <div className="alert alert-info mt-3" role="alert">
            Aucun produit vendeur ne correspond à un produit interne non lié.
          </div>
        )}
        {totalMatchCount > 0 && (
          <Paper className="mt-3" elevation={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Produit aimer.ma</TableCell>
                    <TableCell>Produits internes correspondants</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedMatches.map(({ vendor, matches }) => {
                    const vendorImage = buildVendorImage(vendor.image);
                    return (
                      <TableRow key={vendor.product_id || vendor.name} hover>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              src={vendorImage}
                              alt={vendor.name}
                              sx={{ width: 72, height: 72 }}
                              variant="rounded"
                            />
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {vendor.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID aimer.ma : {vendor.product_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={1}>
                            {matches.map((product) => {
                              const localImage = extractProductImage(product);
                              const localId = product._id || product.id || product.value;
                              const buttonKey = `${vendor.product_id}-${localId}`;
                              const isLinking = linkingTarget === buttonKey;

                              return (
                                <Paper key={buttonKey} variant="outlined" sx={{ p: 1.5 }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    justifyContent="space-between"
                                  >
                                    <Stack direction="row" spacing={2} alignItems="center">
                                      <Avatar
                                        src={localImage}
                                        alt={product.name}
                                        sx={{ width: 72, height: 72 }}
                                        variant="rounded"
                                      >
                                        {product?.name?.[0] || '?'}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                          {product.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {localId}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleLinkProduct(localId, vendor)}
                                      disabled={isLinking}
                                    >
                                      {isLinking ? 'Association…' : 'Lier à aimer.ma'}
                                    </Button>
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalMatchCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Lignes par page"
            />
          </Paper>
        )}
      </section>
    </div>
  );
}

export default AimerPlatformConsole;
